"use client";

import {
  Checkbox,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import type { SingleDistribution } from "@/lib/tool-kit/stats-distributions-more/dataset";
import type { DatasetForm } from "@/lib/tool-kit/stats-distributions-more/dataset-form";

/** Every label the mode-specific fields use; the widget's strings are a superset. */
export interface DatasetFieldLabels {
  distributionLabel: string;
  distNormal: string;
  distUniform: string;
  distSkewed: string;
  distInteger: string;
  distBinary: string;
  paramMean: string;
  paramSd: string;
  paramMin: string;
  paramMax: string;
  paramSkewMin: string;
  paramSkewMean: string;
  paramIntMin: string;
  paramIntMax: string;
  paramBinary: string;
  columnName: string;
  nameXLabel: string;
  meanXLabel: string;
  sdXLabel: string;
  nameYLabel: string;
  meanYLabel: string;
  sdYLabel: string;
  rLabel: string;
  rHint: string;
  groupColumnLabel: string;
  outcomeColumnLabel: string;
  groupNamesLabel: string;
  groupNamesHint: string;
  groupMeansLabel: string;
  groupMeansHint: string;
  groupSdLabel: string;
  exactLabel: string;
  exactHint: string;
}

type Update = (patch: Partial<DatasetForm>) => void;

const PARAMETER_LABELS: Record<
  SingleDistribution,
  [keyof DatasetFieldLabels, keyof DatasetFieldLabels | null]
> = {
  normal: ["paramMean", "paramSd"],
  uniform: ["paramMin", "paramMax"],
  skewed: ["paramSkewMin", "paramSkewMean"],
  integer: ["paramIntMin", "paramIntMax"],
  binary: ["paramBinary", null],
};

export function SingleFields({
  form,
  update,
  labels,
}: {
  form: DatasetForm;
  update: Update;
  labels: DatasetFieldLabels;
}) {
  const [first, second] = PARAMETER_LABELS[form.distribution];
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <SelectField
        id="rd-distribution"
        label={labels.distributionLabel}
        onChange={(distribution) => update({ distribution })}
        options={[
          { value: "normal", label: labels.distNormal },
          { value: "uniform", label: labels.distUniform },
          { value: "skewed", label: labels.distSkewed },
          { value: "integer", label: labels.distInteger },
          { value: "binary", label: labels.distBinary },
        ]}
        value={form.distribution}
      />
      <TextField
        id="rd-value-name"
        label={labels.columnName}
        onChange={(valueName) => update({ valueName })}
        value={form.valueName}
      />
      <TextField
        id="rd-p1"
        label={labels[first]}
        numeric
        onChange={(p1) => update({ p1 })}
        value={form.p1}
      />
      {second === null ? null : (
        <TextField
          id="rd-p2"
          label={labels[second]}
          numeric
          onChange={(p2) => update({ p2 })}
          value={form.p2}
        />
      )}
    </div>
  );
}

export function PairFields({
  form,
  update,
  labels,
}: {
  form: DatasetForm;
  update: Update;
  labels: DatasetFieldLabels;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <TextField
        id="rd-name-x"
        label={labels.nameXLabel}
        onChange={(nameX) => update({ nameX })}
        value={form.nameX}
      />
      <TextField
        id="rd-mean-x"
        label={labels.meanXLabel}
        numeric
        onChange={(meanX) => update({ meanX })}
        value={form.meanX}
      />
      <TextField
        id="rd-sd-x"
        label={labels.sdXLabel}
        numeric
        onChange={(sdX) => update({ sdX })}
        value={form.sdX}
      />
      <TextField
        id="rd-name-y"
        label={labels.nameYLabel}
        onChange={(nameY) => update({ nameY })}
        value={form.nameY}
      />
      <TextField
        id="rd-mean-y"
        label={labels.meanYLabel}
        numeric
        onChange={(meanY) => update({ meanY })}
        value={form.meanY}
      />
      <TextField
        id="rd-sd-y"
        label={labels.sdYLabel}
        numeric
        onChange={(sdY) => update({ sdY })}
        value={form.sdY}
      />
      <TextField
        hint={labels.rHint}
        id="rd-r"
        label={labels.rLabel}
        numeric
        onChange={(r) => update({ r })}
        value={form.r}
      />
    </div>
  );
}

export function GroupFields({
  form,
  update,
  labels,
}: {
  form: DatasetForm;
  update: Update;
  labels: DatasetFieldLabels;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextAreaField
        hint={labels.groupNamesHint}
        id="rd-group-names"
        label={labels.groupNamesLabel}
        onChange={(groupNames) => update({ groupNames })}
        prose
        rows={2}
        value={form.groupNames}
      />
      <TextAreaField
        hint={labels.groupMeansHint}
        id="rd-group-means"
        label={labels.groupMeansLabel}
        onChange={(groupMeans) => update({ groupMeans })}
        rows={2}
        value={form.groupMeans}
      />
      <TextField
        id="rd-group-sd"
        label={labels.groupSdLabel}
        numeric
        onChange={(groupSd) => update({ groupSd })}
        value={form.groupSd}
      />
      <TextField
        id="rd-group-column"
        label={labels.groupColumnLabel}
        onChange={(groupColumn) => update({ groupColumn })}
        value={form.groupColumn}
      />
      <TextField
        id="rd-outcome-column"
        label={labels.outcomeColumnLabel}
        onChange={(outcomeColumn) => update({ outcomeColumn })}
        value={form.outcomeColumn}
      />
    </div>
  );
}

export function ExactToggle({
  checked,
  onChange,
  labels,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  labels: DatasetFieldLabels;
}) {
  return (
    <div className="space-y-1">
      <Checkbox
        checked={checked}
        label={labels.exactLabel}
        onChange={onChange}
      />
      <p className="text-muted-foreground text-xs">{labels.exactHint}</p>
    </div>
  );
}
